import type { ReferenceLocationResponse } from 'types/api-responses';

/**
 * One row of a survey description: a reference location plus the quarter calls
 * that carve it down. Several rows combine into one multi-polygon location.
 */
export interface SurveyRow {
  key: string;
  location?: ReferenceLocationResponse;
  quarterCalls: string[];
}

/** Row keys only need to stay unique for as long as the form is mounted. */
let surveyRowKey = 0;

/**
 * Builds an empty survey row under a fresh key, so React keeps a row's inputs
 * attached to that row as siblings are added and removed around it.
 *
 * @return {SurveyRow} An empty row.
 */
export const emptySurveyRow = (): SurveyRow => ({
  key: `survey-row-${(surveyRowKey += 1)}`,
  quarterCalls: [],
});

/**
 * Adds a survey area picked off the map, filling the first row still waiting for
 * one rather than always appending — so the empty row the section opens with is
 * used up before a second one appears.
 *
 * @param {SurveyRow[]} rows - The current rows.
 * @param {ReferenceLocationResponse} location - The picked survey area.
 * @return {SurveyRow[]} The rows with the survey area in them.
 */
export const addSurveyArea = (
  rows: SurveyRow[],
  location: ReferenceLocationResponse
): SurveyRow[] => {
  const firstEmpty = rows.findIndex((row) => !row.location);

  if (firstEmpty === -1) {
    return [...rows, { ...emptySurveyRow(), location }];
  }

  return rows.map((row, index) => {
    return index === firstEmpty ? { ...row, location } : row;
  });
};

/** A survey area together with the calls that carve it — one tract of a unit. */
export interface SurveyTract {
  location: ReferenceLocationResponse;
  quarterCalls: string[];
}

/**
 * Adds several tracts at once — what clicking a unit produces. Folded into the
 * rows already there, skipping ones already listed, since the same survey area
 * twice is a shape the backend rejects.
 *
 * @param {SurveyRow[]} rows - The current rows.
 * @param {SurveyTract[]} tracts - The tracts to add.
 * @return {SurveyRow[]} The rows with the tracts folded in.
 */
export const addSurveyTracts = (
  rows: SurveyRow[],
  tracts: SurveyTract[]
): SurveyRow[] =>
  tracts.reduce((current, { location, quarterCalls }) => {
    if (current.some((row) => row.location?.id === location.id)) {
      return current;
    }

    const firstEmpty = current.findIndex((row) => !row.location);
    if (firstEmpty === -1) {
      return [...current, { ...emptySurveyRow(), location, quarterCalls }];
    }

    return current.map((row, index) => {
      return index === firstEmpty ? { ...row, location, quarterCalls } : row;
    });
  }, rows);

/**
 * Drops the row holding a survey area, which is what clicking a picked section on
 * the map means. The last row is emptied rather than removed, so the list never
 * collapses to nothing.
 *
 * @param {SurveyRow[]} rows - The current rows.
 * @param {number} referenceLocationId - The survey area to drop.
 * @return {SurveyRow[]} The remaining rows.
 */
export const removeSurveyArea = (
  rows: SurveyRow[],
  referenceLocationId: number
): SurveyRow[] => {
  const remaining = rows.filter(
    (row) => row.location?.id !== referenceLocationId
  );

  return remaining.length > 0 ? remaining : [emptySurveyRow()];
};
